import type { RoutineDraft } from '../settings/importRoutines';
import { bySortIndex } from '../../lib/sortIndex';
import type { Profile, Routine, SyncedTree, Task } from '../../lib/types';
import { uid } from '../../lib/uid';

// Pure helpers for editing the shared routine library and the per-profile
// routineIds references. Each returns a new tree (or the original if the
// change is a no-op) so the caller pipes them through
// useSyncedStore.setTree. See plan tasks/43 §2 for the mutation contract.

function stampTree(tree: SyncedTree, now: string): SyncedTree {
  return { ...tree, meta: { ...tree.meta, lastWriteAt: now } };
}

function patchProfile(
  tree: SyncedTree,
  profileId: string,
  fn: (p: Profile) => Profile,
  now: string,
): SyncedTree {
  const profiles = tree.profiles.map((p) =>
    p.id === profileId ? { ...fn(p), updatedAt: now } : p,
  );
  return stampTree({ ...tree, profiles }, now);
}

function patchLibraryRoutine(
  tree: SyncedTree,
  routineId: string,
  fn: (r: Routine) => Routine,
  now: string,
): SyncedTree {
  const routines = tree.routines.map((r) =>
    r.id === routineId ? { ...fn(r), updatedAt: now } : r,
  );
  return stampTree({ ...tree, routines }, now);
}

// Remove a routine from the library iff no profile still references its id.
// Called by removeRoutine + unassignRoutineFromProfile after they drop the
// id from a profile's routineIds. Intentional: orphaned library entries are
// invisible to the user, and we don't have a separate "cleanup" UI yet.
function pruneOrphanedRoutine(tree: SyncedTree, routineId: string): SyncedTree {
  const stillReferenced = tree.profiles.some((p) => p.routineIds.includes(routineId));
  if (stillReferenced) return tree;
  return { ...tree, routines: tree.routines.filter((r) => r.id !== routineId) };
}

export function addRoutine(
  tree: SyncedTree,
  profileId: string,
  routine: Routine,
  now: string = new Date().toISOString(),
): SyncedTree {
  const nextTree: SyncedTree = { ...tree, routines: [...tree.routines, routine] };
  return patchProfile(
    nextTree,
    profileId,
    (p) => ({ ...p, routineIds: [...p.routineIds, routine.id] }),
    now,
  );
}

// Append a routine to the library without assigning it to any profile. The
// hub uses this to create a blank tasklist that the parent will link to
// children later. Symmetric to duplicateRoutine's "append to end of library,
// leave profiles alone" semantics.
export function createLibraryRoutine(
  tree: SyncedTree,
  routine: Routine,
  now: string = new Date().toISOString(),
): SyncedTree {
  return stampTree({ ...tree, routines: [...tree.routines, routine] }, now);
}

export function updateRoutine(
  tree: SyncedTree,
  routineId: string,
  patch: Partial<Pick<Routine, 'name' | 'emoji'>>,
  now: string = new Date().toISOString(),
): SyncedTree {
  return patchLibraryRoutine(tree, routineId, (r) => ({ ...r, ...patch }), now);
}

export function removeRoutine(
  tree: SyncedTree,
  profileId: string,
  routineId: string,
  now: string = new Date().toISOString(),
): SyncedTree {
  const dropped = patchProfile(
    tree,
    profileId,
    (p) => ({ ...p, routineIds: p.routineIds.filter((id) => id !== routineId) }),
    now,
  );
  return pruneOrphanedRoutine(dropped, routineId);
}

// Hub-level "delete tasklist entirely" — unassign the routine from every
// profile that references it, then drop the library entry. Only profiles
// that actually referenced the id get an updatedAt bump. Unknown id is a
// silent no-op (defensive; the caller UI has already read the id).
export function removeRoutineFromLibrary(
  tree: SyncedTree,
  routineId: string,
  now: string = new Date().toISOString(),
): SyncedTree {
  const touchesProfile = tree.profiles.some((p) => p.routineIds.includes(routineId));
  const touchesLibrary = tree.routines.some((r) => r.id === routineId);
  if (!touchesProfile && !touchesLibrary) return tree;
  const profiles = tree.profiles.map((p) =>
    p.routineIds.includes(routineId)
      ? { ...p, routineIds: p.routineIds.filter((id) => id !== routineId), updatedAt: now }
      : p,
  );
  const routines = tree.routines.filter((r) => r.id !== routineId);
  return stampTree({ ...tree, profiles, routines }, now);
}

export function addTask(
  tree: SyncedTree,
  routineId: string,
  task: Task,
  now: string = new Date().toISOString(),
): SyncedTree {
  return patchLibraryRoutine(tree, routineId, (r) => ({ ...r, tasks: [...r.tasks, task] }), now);
}

export function updateTask(
  tree: SyncedTree,
  routineId: string,
  taskId: string,
  patch: Partial<Pick<Task, 'name' | 'emoji' | 'tag'>>,
  now: string = new Date().toISOString(),
): SyncedTree {
  return patchLibraryRoutine(
    tree,
    routineId,
    (r) => ({
      ...r,
      tasks: r.tasks.map((t) => (t.id === taskId ? { ...t, ...patch, updatedAt: now } : t)),
    }),
    now,
  );
}

export function removeTask(
  tree: SyncedTree,
  routineId: string,
  taskId: string,
  now: string = new Date().toISOString(),
): SyncedTree {
  return patchLibraryRoutine(
    tree,
    routineId,
    (r) => ({ ...r, tasks: r.tasks.filter((t) => t.id !== taskId) }),
    now,
  );
}

// Move a task one step up or down in the displayed (sortIndex-asc) order
// by swapping its sortIndex with its neighbour. No-op at the edge.
export function moveTask(
  tree: SyncedTree,
  routineId: string,
  taskId: string,
  direction: 'up' | 'down',
  now: string = new Date().toISOString(),
): SyncedTree {
  return patchLibraryRoutine(
    tree,
    routineId,
    (r) => {
      const sorted = r.tasks.slice().sort((a, b) => a.sortIndex - b.sortIndex);
      const idx = sorted.findIndex((t) => t.id === taskId);
      if (idx === -1) return r;
      const swapWith = direction === 'up' ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= sorted.length) return r;
      const a = sorted[idx];
      const b = sorted[swapWith];
      if (!a || !b) return r;
      const aIdx = a.sortIndex;
      const bIdx = b.sortIndex;
      return {
        ...r,
        tasks: r.tasks.map((t) => {
          if (t.id === a.id) return { ...t, sortIndex: bIdx, updatedAt: now };
          if (t.id === b.id) return { ...t, sortIndex: aIdx, updatedAt: now };
          return t;
        }),
      };
    },
    now,
  );
}

// Move a task to an arbitrary position by reassigning sequential sortIndex
// values (0, 1, 2, …) to the whole list after the splice.
export function reorderTask(
  tree: SyncedTree,
  routineId: string,
  taskId: string,
  newIndex: number,
  now: string = new Date().toISOString(),
): SyncedTree {
  return patchLibraryRoutine(
    tree,
    routineId,
    (r) => {
      const sorted = r.tasks.slice().sort((a, b) => a.sortIndex - b.sortIndex);
      const from = sorted.findIndex((t) => t.id === taskId);
      if (from === -1) return r;
      const [moved] = sorted.splice(from, 1);
      if (!moved) return r;
      sorted.splice(newIndex, 0, moved);
      const reindexed = sorted.map((t, i) =>
        t.sortIndex === i ? t : { ...t, sortIndex: i, updatedAt: now },
      );
      return { ...r, tasks: reindexed };
    },
    now,
  );
}

// Replace an entire library routine in place — used by the editor's revert
// button to swap the current state for the snapshot captured at mount.
// Unlike the other mutations, this preserves the snapshot's `updatedAt`
// so a revert takes the routine back to a clean state (dirty flag turns
// off). Only bumps meta.lastWriteAt so the change still propagates.
export function replaceRoutine(
  tree: SyncedTree,
  routine: Routine,
  now: string = new Date().toISOString(),
): SyncedTree {
  const routines = tree.routines.map((r) => (r.id === routine.id ? routine : r));
  return stampTree({ ...tree, routines }, now);
}

// Append N routines (typically from a routine-pack JSON import) to one
// profile in a single tree write. Fresh `uid()` for every routine and
// task. Routine `sortIndex` starts at `nextLibrarySortIndex(tree)` and
// increments per draft so the input order is preserved; task `sortIndex`
// is contiguous 0..n−1 within each routine.
//
// Empty drafts is a no-op — returns the input tree unchanged so the
// caller doesn't get a spurious `meta.lastWriteAt` bump for nothing.
export function addRoutinesToProfile(
  tree: SyncedTree,
  profileId: string,
  drafts: RoutineDraft[],
  now: string = new Date().toISOString(),
): SyncedTree {
  if (drafts.length === 0) return tree;
  const profile = tree.profiles.find((p) => p.id === profileId);
  if (!profile) return tree;
  const baseIndex = nextLibrarySortIndex(tree);
  const newRoutines: Routine[] = drafts.map((draft, i) => ({
    id: uid(),
    name: draft.name,
    emoji: draft.emoji,
    sortIndex: baseIndex + i,
    tasks: draft.tasks.map((t, ti) => ({
      id: uid(),
      name: t.name,
      emoji: t.emoji,
      tag: t.tag,
      sortIndex: ti,
      createdAt: now,
      updatedAt: now,
    })),
    createdAt: now,
    updatedAt: now,
  }));
  const withLibrary: SyncedTree = { ...tree, routines: [...tree.routines, ...newRoutines] };
  return patchProfile(
    withLibrary,
    profileId,
    (p) => ({ ...p, routineIds: [...p.routineIds, ...newRoutines.map((r) => r.id)] }),
    now,
  );
}

// Deep-clone a library routine into a fresh entry: new routine + task
// ids, tasks reindexed 0..n−1 in source order, name gets a ' (kopio)'
// suffix (or ' (kopio 2)', ' (kopio 3)', … if the base name is already
// taken). The copy is appended to the library end but NOT assigned to
// any profile — assignment is a separate parent action (see plan
// tasks/43 §"Poista lapselta ≠ Poista kirjastosta"). Throws on unknown
// id so the caller cannot silently no-op into an empty navigation.
const MAX_COPY_SUFFIX = 99;

function nextCopyName(tree: SyncedTree, sourceName: string): string {
  const base = sourceName.replace(/ \(kopio(?: \d+)?\)$/, '');
  const taken = new Set(tree.routines.map((r) => r.name));
  const candidate = `${base} (kopio)`;
  if (!taken.has(candidate)) return candidate;
  for (let n = 2; n <= MAX_COPY_SUFFIX; n++) {
    const c = `${base} (kopio ${n})`;
    if (!taken.has(c)) return c;
  }
  return `${base} (kopio ${MAX_COPY_SUFFIX})`;
}

export function duplicateRoutine(
  tree: SyncedTree,
  routineId: string,
  now: string = new Date().toISOString(),
): { tree: SyncedTree; newRoutineId: string } {
  const source = tree.routines.find((r) => r.id === routineId);
  if (!source) {
    throw new Error(`duplicateRoutine: unknown routine id ${routineId}`);
  }
  const newRoutineId = uid();
  const copy: Routine = {
    id: newRoutineId,
    name: nextCopyName(tree, source.name),
    emoji: source.emoji,
    sortIndex: nextLibrarySortIndex(tree),
    tasks: source.tasks
      .slice()
      .sort((a, b) => a.sortIndex - b.sortIndex)
      .map((t, i) => ({
        id: uid(),
        name: t.name,
        emoji: t.emoji,
        tag: t.tag,
        sortIndex: i,
        createdAt: now,
        updatedAt: now,
      })),
    createdAt: now,
    updatedAt: now,
  };
  const next = stampTree({ ...tree, routines: [...tree.routines, copy] }, now);
  return { tree: next, newRoutineId };
}

// Deep-clone a single task into another (or the same) library routine.
// Fresh uid; `sortIndex` = next slot at the end of the target so the copy
// appends. Throws on unknown ids so a stale UI can't silently no-op.
// target === source is allowed — parents use this to clone-then-tweak.
export function copyTaskToRoutine(
  tree: SyncedTree,
  sourceRoutineId: string,
  taskId: string,
  targetRoutineId: string,
  now: string = new Date().toISOString(),
): SyncedTree {
  const source = tree.routines.find((r) => r.id === sourceRoutineId);
  if (!source) {
    throw new Error(`copyTaskToRoutine: unknown source routine id ${sourceRoutineId}`);
  }
  const sourceTask = source.tasks.find((t) => t.id === taskId);
  if (!sourceTask) {
    throw new Error(`copyTaskToRoutine: unknown task id ${taskId}`);
  }
  const target = tree.routines.find((r) => r.id === targetRoutineId);
  if (!target) {
    throw new Error(`copyTaskToRoutine: unknown target routine id ${targetRoutineId}`);
  }
  const copy: Task = {
    id: uid(),
    name: sourceTask.name,
    emoji: sourceTask.emoji,
    tag: sourceTask.tag,
    sortIndex: nextTaskSortIndex(target),
    createdAt: now,
    updatedAt: now,
  };
  return patchLibraryRoutine(
    tree,
    targetRoutineId,
    (r) => ({ ...r, tasks: [...r.tasks, copy] }),
    now,
  );
}

// Idempotent: assigning an already-assigned routine returns the tree
// unchanged. Otherwise the id is appended to the end of routineIds.
export function assignRoutineToProfile(
  tree: SyncedTree,
  profileId: string,
  routineId: string,
  now: string = new Date().toISOString(),
): SyncedTree {
  const profile = tree.profiles.find((p) => p.id === profileId);
  if (!profile) return tree;
  if (profile.routineIds.includes(routineId)) return tree;
  return patchProfile(
    tree,
    profileId,
    (p) => ({ ...p, routineIds: [...p.routineIds, routineId] }),
    now,
  );
}

// Remove one profile's assignment. Runs the same orphan cleanup as
// removeRoutine — if no profile still references the id, drop the entry
// from the library too.
export function unassignRoutineFromProfile(
  tree: SyncedTree,
  profileId: string,
  routineId: string,
  now: string = new Date().toISOString(),
): SyncedTree {
  const profile = tree.profiles.find((p) => p.id === profileId);
  if (!profile) return tree;
  if (!profile.routineIds.includes(routineId)) return tree;
  const dropped = patchProfile(
    tree,
    profileId,
    (p) => ({ ...p, routineIds: p.routineIds.filter((id) => id !== routineId) }),
    now,
  );
  return pruneOrphanedRoutine(dropped, routineId);
}

// Replace a profile's routineIds with a new order. Unknown ids in
// `orderedIds` are dropped (so a stale caller can't inject junk); ids
// currently on the profile that are missing from `orderedIds` are also
// dropped, matching a full-list replace semantic used by dnd-kit.
export function reorderProfileRoutines(
  tree: SyncedTree,
  profileId: string,
  orderedIds: string[],
  now: string = new Date().toISOString(),
): SyncedTree {
  const profile = tree.profiles.find((p) => p.id === profileId);
  if (!profile) return tree;
  const current = new Set(profile.routineIds);
  const filtered = orderedIds.filter((id) => current.has(id));
  // Reference-equal no-op when the order didn't change.
  if (
    filtered.length === profile.routineIds.length &&
    filtered.every((id, i) => id === profile.routineIds[i])
  ) {
    return tree;
  }
  return patchProfile(tree, profileId, (p) => ({ ...p, routineIds: filtered }), now);
}

// Resolves a profile's routineIds to concrete Routine entities in
// per-profile order. Missing ids are skipped silently — a missing entry
// is either an in-flight sync race or an orphaned pointer we cleaned up
// on the library side; either way the UI shouldn't crash on it.
export function getProfileRoutines(tree: SyncedTree, profileId: string): Routine[] {
  const profile = tree.profiles.find((p) => p.id === profileId);
  if (!profile) return [];
  const library = new Map(tree.routines.map((r) => [r.id, r]));
  const out: Routine[] = [];
  for (const id of profile.routineIds) {
    const r = library.get(id);
    if (r) out.push(r);
  }
  return out;
}

// Inverse of getProfileRoutines: every profile whose routineIds includes
// the given id, sorted by the profile's own sortIndex (with createdAt as
// the stable tiebreaker via bySortIndex). The hub uses this to render
// which children a library routine is currently linked to.
export function getProfilesForRoutine(tree: SyncedTree, routineId: string): Profile[] {
  return tree.profiles
    .filter((p) => p.routineIds.includes(routineId))
    .slice()
    .sort(bySortIndex);
}

// Next sortIndex for a new library routine — computed across the whole
// library so ordering stays stable no matter which profile triggered the
// add. Empty library → 0.
export function nextLibrarySortIndex(tree: SyncedTree): number {
  if (tree.routines.length === 0) return 0;
  return Math.max(...tree.routines.map((r) => r.sortIndex)) + 1;
}

export function nextTaskSortIndex(routine: Routine): number {
  if (routine.tasks.length === 0) return 0;
  return Math.max(...routine.tasks.map((t) => t.sortIndex)) + 1;
}
